/**
 * Suspense-enabled hook for a company-private root's direct sub-categories.
 *
 * The company-exclusive content tree is nested: the company root category
 * (identifier === gymId) holds ZERO entries directly — its content lives one
 * level deeper, under sub-categories (e.g. "Healthy Menu Plan"). So the
 * `/company` index must render the root's direct children and let each
 * sub-category fetch its own entries (mirroring the native app and the
 * Academy flow), instead of querying entries at the root parentId (which
 * always returns []).
 *
 * Accepts the root's UUID or its identifier (gymId) — both resolve via
 * `resolveCategoryId`. Mirrors `useExploreCategoriesSuspense`, including the
 * `isPublished` filter so unpublished / future-dated sub-categories never leak.
 */
import { useSuspenseQuery } from '@tanstack/react-query';
import { isPublished } from '../Utils/isPublished';
import { getAllExploreCategoriesV3, resolveCategoryId } from '../Model/Explore/exploreCategoryTreeV3';

export function useCompanySubCategoriesSuspense(rootIdOrIdentifier?: string) {
  const { data } = useSuspenseQuery({
    queryKey: ['companySubCategoriesV3', rootIdOrIdentifier ?? null],
    queryFn: async () => {
      if (!rootIdOrIdentifier) return [];
      const all = await getAllExploreCategoriesV3();
      const rootId = resolveCategoryId(all, rootIdOrIdentifier);
      if (!rootId) return [];
      return all.filter((c) => (c.parentIds ?? []).includes(rootId)).filter(isPublished);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const categories = data ?? [];

  return { categories };
}
